import { createContext, useContext, useState } from "react"
import { toast } from 'sonner'
import { handleError } from '@/services/errorHandler.js'

export const LibraryContext = createContext(null)

export function LibraryProvider({ children }) {
    // Tags state
    const [tags, setTags] = useState([
        { id: 1, name: 'B Roll' },
        { id: 1, name: 'Product Demo' },
        { id: 2, name: 'Testimonial' },
        { id: 3, name: 'Tutorial' },
        { id: 4, name: 'Explainer' },
        { id: 5, name: 'Promotional' },
        { id: 6, name: 'Unboxing' },
        { id: 7, name: 'Problem' },
        { id: 8, name: 'CTA' },
        { id: 9, name: 'Customer Review' }
    ])

    // AI suggested tags
    const [aiSuggestedTags, setAiSuggestedTags] = useState([
        { id: 101, name: 'Promotional' },
        { id: 102, name: 'Outdoor activity' },
        { id: 103, name: 'Family time' },
        { id: 104, name: 'Travel' },
        { id: 105, name: 'Singing' },
    ])

    // Boards state
    const [boards, setBoards] = useState([
        { id: 1, name: 'Q2 Campaign', clipCount: 12 },
        { id: 2, name: 'Summer Sale', clipCount: 8 },
        { id: 3, name: 'Product Launch', clipCount: 5 }
    ])

    // Selected tag and filter state
    const [selectedTag, setSelectedTag] = useState(null)
    const [activeFilter, setActiveFilter] = useState('all') // 'all' or 'untagged' or 'tag'
    const [selectedBoard, setSelectedBoard] = useState(null)

    // Get all tags
    const getAllTags = () => {
        return tags
    }

    // Get all AI suggested tags
    const getAiSuggestedTags = () => {
        return aiSuggestedTags
    }

    // Get all boards
    const getAllBoards = () => {
        return boards
    }

    // Handle tag selection
    const handleTagSelect = (tag) => {
        if (selectedTag && selectedTag.id === tag.id) {
            // If clicking the same tag again, clear it
            setSelectedTag(null)
            setActiveFilter('all')
        } else {
            // Select the new tag
            setSelectedTag(tag)
            setActiveFilter('tag')
        }
        setSelectedBoard(null) // Clear selected board when selecting a tag
    }

    // Handle filter selection
    const handleFilterSelect = (filter) => {
        setActiveFilter(filter)
        setSelectedTag(null) // Clear selected tag when switching filters
        setSelectedBoard(null) // Clear selected board when switching filters
    }

    // Handle board selection
    const handleBoardSelect = (board) => {
        setSelectedBoard(board)
        setSelectedTag(null) // Clear selected tag when selecting a board
    }

    // Add a new tag
    const addTag = (tagName) => {
        try {
            if (!tagName || tagName.trim() === '') {
                return
            }

            // Check if tag already exists
            if (tags.some(tag => tag.name.toLowerCase() === tagName.toLowerCase())) {
                toast.error('Tag already exists')
                return
            }

            // Create new tag with unique ID
            const newTag = {
                id: Math.max(...tags.map(tag => tag.id), 0) + 1,
                name: tagName.trim()
            }

            setTags([...tags, newTag])
            toast.success('Tag added successfully')
            return newTag
        } catch (e) {
            handleError(e)
            console.error(e)
        }
    }

    // Remove a tag
    const removeTag = (tagId) => {
        try {
            setTags(tags.filter(tag => tag.id !== tagId))
            toast.success('Tag removed successfully')
        } catch (e) {
            handleError(e)
            console.error(e)
        }
    }

    // Add a new board
    const addBoard = (boardName) => {
        try {
            if (!boardName || boardName.trim() === '') {
                return
            }

            // Check if board already exists
            if (boards.some(board => board.name.toLowerCase() === boardName.toLowerCase())) {
                toast.error('Board already exists')
                return
            }

            // Create new board with unique ID
            const newBoard = {
                id: Math.max(...boards.map(board => board.id), 0) + 1,
                name: boardName.trim(),
                clipCount: 0
            }

            setBoards([...boards, newBoard])
            toast.success('Board added successfully')
            return newBoard
        } catch (e) {
            handleError(e)
            console.error(e)
        }
    }

    // Remove a board
    const removeBoard = (boardId) => {
        try {
            setBoards(boards.filter(board => board.id !== boardId))
            toast.success('Board removed successfully')
        } catch (e) {
            handleError(e)
            console.error(e)
        }
    }

    return (
        <LibraryContext.Provider
            value={{
                tags,
                aiSuggestedTags,
                boards,
                getAllTags,
                getAiSuggestedTags,
                getAllBoards,
                addTag,
                removeTag,
                addBoard,
                removeBoard,
                selectedTag,
                activeFilter,
                selectedBoard,
                handleTagSelect,
                handleFilterSelect,
                handleBoardSelect
            }}>
            {children}
        </LibraryContext.Provider>
    )
}

export const useLibraryContext = () => {
    const context = useContext(LibraryContext)

    if (!context) {
        throw new Error('useLibraryContext must be used inside LibraryProvider')
    }

    return context
}
